import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { AngularContext } from 'react-app';

// Form
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Types
import {
  LiveEvent,
  LiveEventSession,
  SessionTypes,
  getDefaultLiveEvent,
  SessionSettings,
  getLiveEventPayload,
  Draft,
  UpdateRegistrationStatus,
  LiveEventRegistrationLimit,
} from 'redux/schemas/models/live-event';
import {
  getSessionSchema,
  getSessionSettingsSchema,
} from 'redux/schemas/models/live-event-schema';

// Components
import { Button } from 'react-bootstrap';
import _ from 'underscore';
import { config } from '../../../config/pendo.config.json';
import LiveEventSessions from './live-event-sessions';
import LiveEventSettings from './live-event-settings';
import MultiSessionWarningModal from './multi-session-warning-modal';
import RegistrationLimit from './registration-limit';

export const LiveSessionFormContext = createContext(null);

export type AngularLiveEvent = {
  currentTimeZone: string
  id?: number
  sessions: LiveEventSession[]
  mainTitle: string
  mainDescription?: string
};

type LiveSessionFormProps = {
  draft: AngularLiveEvent
  sessionType: SessionTypes
  lectureComponent: any // TODO: Set the correct type
  msTeamsEmail: string
  creatorUserCourseId: number
  durations: number[]
  isLinked: boolean
  isContentManagementCollection: boolean
  isAccountLevel: boolean
  save: () => void
  cancel: () => void
  showZoomHostValidator: () => boolean
  deleteSession: (sessionId: number, onSuccess? : (value: unknown) => void) => void
  hasRecordingScope: boolean
  isReleased?: boolean
};

const LiveSessionForm = ({
  draft,
  sessionType = SessionTypes.EXTERNAL,
  lectureComponent,
  creatorUserCourseId,
  isLinked,
  isContentManagementCollection,
  isAccountLevel,
  save,
  cancel,
  deleteSession,
  hasRecordingScope,
  isReleased,
  ...restProps
}: LiveSessionFormProps) => {
  const { injectServices } = useContext(AngularContext);
  const [CurrentUserManager] = injectServices(['CurrentUserManager']);
  const timezone = CurrentUserManager.user.timeZone ?? CurrentUserManager.user.browserTimeZone;
  const [isValidHost, setIsValidHost] = useState(false);
  const [sessionsDeleted, setSessionsDeleted] = useState<number[]>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isUpdating, setIsupdating] = useState(false);

  const [payLoad, setPayload] = useState<LiveEvent>();

  const hasMultipleSessions = draft?.sessions?.length > 1;

  const sessionSchema: yup.Schema<LiveEventSession> = getSessionSchema({
    sessionType,
    isContentManagementCollection,
    errorMessages: {
      required: t.VALIDATION.REQUIRED(),
      url: t.VALIDATION.URL(),
      email: t.VALIDATION.EMAIL(),
    },
    isAccountLevel,
  });
  const settingsSchema: yup.Schema<SessionSettings> = getSessionSettingsSchema(sessionType);

  const validationSchema: yup.Schema<LiveEvent> = yup.object().shape({
    sessionType: yup.string().oneOf([SessionTypes.EXTERNAL, SessionTypes.MS_TEAMS, SessionTypes.ZOOM]).required(),
    multisessionSetting: yup.boolean().required(),
    sessions: yup.array().of(sessionSchema),
    settings: settingsSchema,
    isRegistrationLimitEnabled: yup.boolean(),
    registrationLimit: yup.number()
      .transform((value, originalValue) => (originalValue === '' || Number.isNaN(originalValue) ? null : value))
      .nullable()
      .max(LiveEventRegistrationLimit[sessionType], t.VALIDATION.MAX(`${LiveEventRegistrationLimit[sessionType]}`)),
  });

  const defaultLiveEvent: LiveEvent = getDefaultLiveEvent(
    sessionType,
    ({ ...draft, timezone } as Draft),
    isContentManagementCollection,
    isLinked,
    isAccountLevel,
    hasRecordingScope,
  );

  const methods = useForm<LiveEvent>({
    mode: 'all',
    defaultValues: defaultLiveEvent,
    resolver: yupResolver(validationSchema),
  });
  const { handleSubmit, formState: { isDirty, isSubmitting, isValid, dirtyFields } } = methods;

  const submit = () => {
    setIsupdating(!!draft.id);
    // Use the getValues method to get the form data
    // instead of the data sent to this function
    // is applying a .toString() to the start time property
    // and it's causing the date to be invalid
    // because it's using the format from the language
    const data = methods.getValues();
    lectureComponent.getPayload = () => getLiveEventPayload(
      data,
      creatorUserCourseId,
      sessionType,
      isAccountLevel,
      draft,
      isContentManagementCollection,
    );
    setPayload(data);
    if (sessionsDeleted.length === 0) {
      if (!hasMultipleSessions
        && draft?.id
        && data.sessions.length > 1
        && isReleased) {
        setShowWarningModal(true);
      } else {
        save();
      }
    } else {
      const promises = sessionsDeleted.map((sessionId) => new Promise((resolve) => deleteSession(sessionId, resolve)));
      Promise.all(promises).then(save);
    }
  };

  const onUpdateToMultiSession = (registerOption: UpdateRegistrationStatus) => {
    setShowWarningModal(false);
    lectureComponent.getPayload = () => getLiveEventPayload(
      payLoad,
      creatorUserCourseId,
      sessionType,
      isAccountLevel,
      draft,
      isContentManagementCollection,
      registerOption,
    );
    save();
  };

  const onHostValidation = (isFormValid: boolean) => {
    setIsValidHost(isFormValid);
  };

  const handleSessionDeleted = (id: number) => {
    setSessionsDeleted([...sessionsDeleted, id]);
  };

  useEffect(() => {
    const hasDeletedSessions = sessionsDeleted.length > 0;
    const shouldDisable = _.isEmpty(dirtyFields) || (!isValid && !hasDeletedSessions);

    const shouldDisableSubmit = isSubmitting
    || !isDirty
    || shouldDisable
    || (isAccountLevel && !isValidHost && !isContentManagementCollection);

    if (shouldDisableSubmit !== isSubmitDisabled) {
      setIsSubmitDisabled(shouldDisableSubmit);
    }
  }, [
    dirtyFields.sessions, isAccountLevel, isContentManagementCollection, isSubmitting,
    isDirty, isValid, isSubmitDisabled, isValidHost, sessionsDeleted.length,
  ]);

  useEffect(() => () => {
    methods.reset();
  }, []);

  const getSubmitButtonText = useCallback(() => {
    if (isSubmitting && !draft.id) return t.FORM.ADDING();
    if (draft.id) return isUpdating ? t.FORM.UPDATING() : t.FORM.UPDATE();
    return t.FORM.ADD();
  }, [draft.id, isSubmitting, isUpdating]);

  return (
    <LiveSessionFormContext.Provider value={{
      sessionType,
      onHostValidation,
      onSessionDeleted: handleSessionDeleted,
      getDurationText: lectureComponent.durationText,
      settingsDisabled: isContentManagementCollection && !!draft.id,
      isContentManagementCollection,
      isLinked,
      catalogId: lectureComponent.catalogId,
      isAccountLevel,
      ...restProps,
    }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          {/* Live event sessions */}
          <LiveEventSessions />

          {/* Live event settings */}
          {sessionType !== SessionTypes.EXTERNAL && (
            <LiveEventSettings />
          )}
          <RegistrationLimit />

          {/* Buttons bar */}
          <div className='mt-4 text-align-center'>
            <Button
              type='button'
              variant='secondary'
              onClick={cancel}
              className='mr-2'
              data-qa={config.pendo.liveEvent.liveEventSession.cancel}
            >
              {t.FORM.CANCEL()}
            </Button>
            <Button
              type='submit'
              disabled={isSubmitDisabled}
              data-qa={config.pendo.liveEvent.liveEventSession.save}
            >
              {getSubmitButtonText()}
            </Button>
          </div>
        </form>
      </FormProvider>
      {showWarningModal && (
        <MultiSessionWarningModal
          onCancel={() => { setShowWarningModal(false); setIsupdating(false); }}
          onUpdate={onUpdateToMultiSession}
        />
      )}
    </LiveSessionFormContext.Provider>
  );
};

export default LiveSessionForm;
